import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config/api';

interface User {
  email: string;
  name: string;
  is_admin: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  loginAsUser: (targetEmail: string) => Promise<boolean>;
  logout: () => Promise<void>;
  user: User | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user is already logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        try {
          // Verify token by getting current user
          const response = await axios.get(`${getApiUrl()}/api/auth/me`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          if (response.data.success) {
            setIsAuthenticated(true);
            setUser(response.data.data);
          } else {
            // Token is invalid, remove it
            localStorage.removeItem('admin_token');
          }
        } catch (error) {
          // Token is invalid or expired, remove it
          localStorage.removeItem('admin_token');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const response = await axios.post(`${getApiUrl()}/api/auth/authenticate`, {
        username,
        password
      });

      if (response.data.success) {
        const { token, user } = response.data.data;
        localStorage.setItem('admin_token', token);
        setIsAuthenticated(true);
        setUser(user);
        return true;
      }
      return false;
    } catch (error: any) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (token) {
        // Call logout endpoint
        await axios.post(`${getApiUrl()}/api/auth/logout`, {}, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local state regardless of API call success
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('admin_token');
    }
  };

  const loginAsUser = async (targetEmail: string): Promise<boolean> => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        return false;
      }

      const response = await axios.post(`${getApiUrl()}/api/auth/login-as`, 
        { targetEmail },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        const { token: newToken, user } = response.data.data;
        localStorage.setItem('admin_token', newToken);
        setIsAuthenticated(true);
        setUser(user);
        return true;
      }
      return false;
    } catch (error: any) {
      console.error('Login as user error:', error);
      return false;
    }
  };

  const value = {
    isAuthenticated,
    login,
    loginAsUser,
    logout,
    user,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
